import { Button, Dialog, MobileDrawer } from "@jugl-web/ui-kit";
import { useAppVariant, useTranslations } from "@jugl-web/utils";
import { FC, useCallback } from "react";
import { ReactComponent as ArrowRightIcon } from "./assets/arrow-right.svg";

export interface DeleteTicketDialogProps {
  isOpen: boolean;
  ticketUniqueId: string;
  ticketOwnerName: string;
  onClose: () => void;
  onConfirm: () => void;
}

export const DeleteTicketDialog: FC<DeleteTicketDialogProps> = ({
  isOpen,
  ticketUniqueId,
  ticketOwnerName,
  onClose,
  onConfirm,
}) => {
  const { isMobile } = useAppVariant();
  const { t } = useTranslations();

  const handleConfirm = useCallback(() => {
    onConfirm();
    onClose();
  }, [onClose, onConfirm]);

  const title = t({
    id: "client-support-page.delete-ticket-emoji",
    defaultMessage: "🗑️ Delete Ticket",
  });

  const description = t(
    {
      id: "client-support-page.delete-ticket-description",
      defaultMessage:
        "Are you sure you want to delete ticket {ticketId} for {name}? This action can't be undone.",
    },
    {
      ticketId: (
        <span className="bg-linear-to-r from-[#2196F3] to-[#8A41D3] bg-clip-text font-medium text-transparent">
          #{ticketUniqueId}
        </span>
      ),
      name: <span className="font-medium text-primary">{ticketOwnerName}</span>,
    }
  );

  const cancelLabel = t({
    id: "common.cancel",
    defaultMessage: "Cancel",
  });

  const deleteLabel = t({
    id: "common.delete",
    defaultMessage: "Delete",
  });

  if (isMobile) {
    return (
      <MobileDrawer
        isOpen={isOpen}
        size="auto"
        header={{ title, hasCloseButton: false }}
        onClose={onClose}
      >
        <MobileDrawer.Content>
          <span className="font-secondary text-sm leading-[21px] text-dark-700">
            {description}
          </span>
        </MobileDrawer.Content>
        <MobileDrawer.Actions isHorizontal className="gap-4 pb-0">
          <Button variant="tertiary" onClick={onClose} className="flex-1">
            {cancelLabel}
          </Button>
          <Button variant="danger" onClick={handleConfirm} className="flex-1">
            {deleteLabel}
          </Button>
        </MobileDrawer.Actions>
      </MobileDrawer>
    );
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      className="flex w-[480px] flex-col overflow-hidden rounded-2xl p-10"
    >
      <span className="font-secondary text-[22px] font-bold text-dark">
        {title}
      </span>
      <span className="mt-2.5 font-secondary text-sm leading-[21px] text-dark-700">
        {description}
      </span>
      <div className="mt-14 flex items-center gap-4">
        <Button onClick={onClose} variant="tertiary" className="w-[140px]">
          {cancelLabel}
        </Button>
        <Button
          onClick={handleConfirm}
          iconEnd={<ArrowRightIcon />}
          className="flex-1"
          variant="danger"
        >
          {deleteLabel}
        </Button>
      </div>
    </Dialog>
  );
};
